/**
 * 3D 导演台视口（P0-3c：多对象 + 拾取 + 三变换 gizmo）
 *
 * 渲染源是 store.project.objects（数组→逐个 SceneObject），不再是单模型演示。
 * gizmo 只附着单选对象；拖拽经 onObjectChange 回写 store（角度存度数、位置/缩放存米），
 * gridSnap 开启时平移按 GRID_SNAP_STEP_M 吸附。
 *
 * 接线约定（历轮实测定案，勿改）：
 *  · 画质一律走 QualityPreset；颜色全部来自 store/模型层常量，组件内禁止色值字面量；
 *  · 模型四步准备（规范化→剔道具→拟合→rest）在 modelCache.acquireCharacter 内，
 *    姿势修正 (rest∘std¹)∘pose 在 poseApply.applyBoneMap——视口只负责摆放与交互；
 *  · 右键不绑任何动作（Edge 按钮手势压不住，让位给 P0-3c 之后的上下文菜单），
 *    平移=中键，缩放=滚轮。
 */
import { useEffect, useState } from 'react';
import { Canvas, useThree } from '@react-three/fiber';
import { Grid, Html, OrbitControls, TransformControls } from '@react-three/drei';
import { Box3, Box3Helper, Color, MOUSE, Raycaster, Vector2, Vector3 } from 'three';
import type { Object3D } from 'three';

import { GRID_SNAP_STEP_M, SELECTION_COLOR } from '../../model/director';
import type { QualityPreset } from '../../platform/qualityPresets';
import { effectivePixelRatio } from '../../platform/qualityPresets';
import { CameraDirector } from './CameraDirector';
import { CameraGizmos, CameraGizmoControls, CameraPiP } from './CameraGizmos';
import { BoneGizmo } from './BoneGizmo';
import { ViewCommander } from './ViewCommander';
import { isClickLike } from './cameraMath';
import { getRuntime, subscribeRuntime } from './runtimeRegistry';
import { SceneObject } from './SceneObject';
import { useDirectorStore } from './store';

export type GizmoMode = 'translate' | 'rotate' | 'scale';

interface Props {
  preset: QualityPreset;
  gizmoMode: GizmoMode;
  onError: (message: string) => void;
  /** 每次拾取点击的诊断信息（射线命中数/归属/屏幕兑底距离），让选不中这类问题能当场归因 */
  onPickDebug?: (message: string) => void;
  /** 导出当前视口渲染为 PNG data-URI 的句柄（供“落成关键帧”回写画布） */
  captureRef?: { current: (() => string | null) | null } | undefined;
}

const RAD_TO_DEG = 180 / Math.PI;

/** 手动射线拾取 + 屏幕空间邻近兑底（第七/八轮实测教训）：
 *  · 源码铁证：three TransformControls 对**每个** pointerdown 无条件 setPointerCapture
 *    并接管事件链（TransformControls.js L1035-1049），R3F 声明式 onClick 与单一射线方案都可能被吞；
 *  · 双保险：射线命中优先；无归属时用"包围盒 8 角投影的屏幕方框包含点击点"兑底
 *    （第十轮升级：旧中心点距离判据对细长人形太脆），保证选中永远有路；
 *  · 每次点击输出诊断（命中数/归属/屏幕框证据），再选不中状态栏直接给答案；
 *  · 单选制（用户裁定）：点中谁选谁，禁止群体调整；点空白不清除；拖拽松手不算点击。 */

function ClickPicker({
  onPickDebug,
}: {
  onPickDebug?: ((m: string) => void) | undefined;
}) {
  const { gl, camera } = useThree();
  useEffect(() => {
    const el = gl.domElement;
    const raycaster = new Raycaster();
    const ndc = new Vector2();
    let down: { x: number; y: number; t: number } | null = null;

    function onPointerDown(e: PointerEvent): void {
      if (e.button === 0) {
        down = { x: e.clientX, y: e.clientY, t: performance.now() };
      }
    }

    function onPointerUp(e: PointerEvent): void {
      if (e.button !== 0 || down === null) {
        return;
      }
      const start = down;
      down = null;
      if (!isClickLike(start, { x: e.clientX, y: e.clientY, t: performance.now() })) {
        return;
      }
      const rect = el.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) {
        return;
      }
      ndc.set(
        ((e.clientX - rect.left) / rect.width) * 2 - 1,
        -((e.clientY - rect.top) / rect.height) * 2 + 1,
      );
      raycaster.setFromCamera(ndc, camera);
      const store = useDirectorStore.getState();
      const targets: Object3D[] = [];
      for (const o of store.project.objects) {
        if (!o.visible || o.locked) {
          continue;
        }
        const entry = getRuntime(o.id);
        if (entry !== null) {
          targets.push(entry.group);
        }
      }
      const hits = raycaster.intersectObjects(targets, true);
      // 诊断增强（第九轮）：命中对象身份 + 各目标 group 是否真挂在场景图里
      // （parent 链能通到 Scene 才算活；不通即 StrictMode/重建产生的幽灵引用）
      const hitDesc =
        hits.length > 0
          ? `命中[${hits[0]?.object.name || '无名'}·${hits[0]?.object.type}]`
          : '命中0';
      const attachDesc = targets
        .map((g) => {
          let n: Object3D | null = g;
          let attached = false;
          while (n !== null) {
            if (n.type === 'Scene') {
              attached = true;
              break;
            }
            n = n.parent;
          }
          const gid = (g.userData as { ownerId?: unknown }).ownerId;
          return `${typeof gid === 'string' ? gid : '?'}:${attached ? '活' : 'DETACHED'}`;
        })
        .join(',');
      let owner: string | null = null;
      for (const h of hits) {
        let node: Object3D | null = h.object;
        while (node !== null) {
          const o = (node.userData as { ownerId?: unknown }).ownerId;
          if (typeof o === 'string') {
            owner = o;
            break;
          }
          node = node.parent;
        }
        if (owner !== null) {
          break;
        }
      }

      // 数据驱动屏幕胶囊拾取（第十一轮定案，万镜一刻同路做法）：
      // 不依赖场景图反推包围盒（诊断实测 Box3.setFromObject 在本场景不可靠），
      // 而用工程数据本身：脚底=transform.position，头顶=+身高×uniformScale，
      // 两点投影成屏幕矩形（横向±90px 容忍展臂/道具），中心最近者当选。
      let rectDesc = '';
      if (owner === null) {
        const v = new Vector3();
        let bestId: string | null = null;
        let bestD = Infinity;
        const parts: string[] = [];
        for (const o of store.project.objects) {
          if (!o.visible || o.locked) {
            continue;
          }
          const [x, y, z] = o.transform.position;
          const h = (o.kind === 'character' ? 1.8 : 1.2) * o.uniformScale;
          v.set(x, y, z).project(camera);
          const fx = rect.left + (v.x * 0.5 + 0.5) * rect.width;
          const fy = rect.top + (-v.y * 0.5 + 0.5) * rect.height;
          v.set(x, y + h, z).project(camera);
          const hx = rect.left + (v.x * 0.5 + 0.5) * rect.width;
          const hy = rect.top + (-v.y * 0.5 + 0.5) * rect.height;
          const x0 = Math.min(fx, hx) - 90;
          const x1 = Math.max(fx, hx) + 90;
          const y0 = Math.min(fy, hy) - 30;
          const y1 = Math.max(fy, hy) + 30;
          parts.push(`${o.id}:[${Math.round(x0)},${Math.round(y0)}~${Math.round(x1)},${Math.round(y1)}]`);
          if (e.clientX >= x0 && e.clientX <= x1 && e.clientY >= y0 && e.clientY <= y1) {
            const d = Math.hypot((x0 + x1) / 2 - e.clientX, (y0 + y1) / 2 - e.clientY);
            if (d < bestD) {
              bestD = d;
              bestId = o.id;
            }
          }
        }
        owner = bestId;
        rectDesc =
          `｜点击(${Math.round(e.clientX)},${Math.round(e.clientY)})`
          + ` rect[${Math.round(rect.left)},${Math.round(rect.top)},${Math.round(rect.width)}x${Math.round(rect.height)}]`
          + ` cam(${camera.position.x.toFixed(1)},${camera.position.y.toFixed(1)},${camera.position.z.toFixed(1)})`
          + ` ${parts.join(' ')}`;
      }

      onPickDebug?.(
        `拾取：${hitDesc}，归属 ${owner ?? '无'}，目标[${attachDesc}]${rectDesc}`,
      );
      if (owner !== null) {
        store.selectObjects([owner]); // 单选：永远替换，不追加
      }
      // 无归属：保持当前选中（用户规则：点空白不清除，gizmo 常驻）
    }

    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointerup', onPointerUp);
    return () => {
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('pointerup', onPointerUp);
    };
  }, [gl, camera, onPickDebug]);
  return null;
}

/** 选中线框盒统一在 Canvas 根层渲染（世界空间）。
 *  第六轮实测 bug 的根治：世界坐标包围盒若挂在对象 group 下会被再乘一次变换，
 *  选中框飞到远处——量测空间与渲染空间必须一致。
 *  重算时机：选中集变化 / 对象增删或 transform 提交 / runtimeRegistry 通知（加载完成、套姿势后 touch）。 */
function SelectionBoxes() {
  const selectedIds = useDirectorStore((s) => s.selectedObjectIds);
  const objects = useDirectorStore((s) => s.project.objects);
  const [version, setVersion] = useState(0);
  useEffect(() => subscribeRuntime(() => setVersion((v) => v + 1)), []);
  const [helpers, setHelpers] = useState<Box3Helper[]>([]);

  useEffect(() => {
    const next: Box3Helper[] = [];
    for (const id of selectedIds) {
      const entry = getRuntime(id);
      if (entry === null) {
        continue;
      }
      const box = new Box3();
      if (entry.bounds !== undefined) {
        // 角色：用加载时算好的可靠 bounds（内层 group 局部空间），经内层 matrixWorld 转世界。
        // 不再对活体蒙皮克隆用 Box3.setFromObject——那会测出错误小盒钉在原点（D-28）。
        const { center, size } = entry.bounds;
        box.min.set(center[0] - size[0] / 2, center[1] - size[1] / 2, center[2] - size[2] / 2);
        box.max.set(center[0] + size[0] / 2, center[1] + size[1] / 2, center[2] + size[2] / 2);
        const inner = entry.group.children[0] ?? entry.group;
        inner.updateWorldMatrix(true, false);
        box.applyMatrix4(inner.matrixWorld);
      } else {
        // 道具：静态几何体，Box3.setFromObject 可靠
        box.setFromObject(entry.group);
      }
      if (box.isEmpty()) {
        continue;
      }
      next.push(new Box3Helper(box, new Color(SELECTION_COLOR)));
    }
    setHelpers((prev) => {
      for (const h of prev) {
        h.dispose();
      }
      return next;
    });
  }, [selectedIds, objects, version]);

  return (
    <>
      {helpers.map((h) => (
        <primitive key={h.uuid} object={h} />
      ))}
    </>
  );
}

/** 各体型实测身高（probe_glb_bounds 实算，GLB 已归一到米）：标签高度按体型自适应。
 *  新增体型时补一行即可；比依赖异步 bounds 更确定，不受注册表时序竞争影响。 */
const BODY_TOP_M: Record<string, number> = {
  standard: 1.74,
  robust: 2.18,
  broad: 1.75,
  child: 1.74,
};

/** 对象名称标签（世界层、由 store objects 驱动）：每个可见对象头顶一个 chip。
 *  不依赖模型异步加载时机，修复“刷新后多角色只剩一个标签”；高度按体型查表，修复高个子标签脸前。 */
function ObjectLabels() {
  const objects = useDirectorStore((s) => s.project.objects);
  const showLabels = useDirectorStore((s) => s.project.scene.showLabels);
  if (!showLabels) {
    return null;
  }
  return (
    <>
      {objects.map((o) => {
        if (!o.visible) {
          return null;
        }
        const top =
          o.kind === 'character' ? BODY_TOP_M[o.bodyType ?? 'standard'] ?? 1.74 : 1.6;
        const y = o.transform.position[1] + top * o.uniformScale + 0.15;
        return (
          <group key={o.id} position={[o.transform.position[0], y, o.transform.position[2]]}>
            <Html center distanceFactor={7} zIndexRange={[15, 0]} style={{ pointerEvents: 'none' }}>
              <span className="flex items-center gap-1 whitespace-nowrap rounded-control border border-surface-edge bg-surface-overlay/90 px-1.5 py-0.5 text-[11px] text-ink-secondary shadow-[0_1px_4px_rgba(0,0,0,.4)]">
                <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: o.tint }} />
                {o.name}
              </span>
            </Html>
          </group>
        );
      })}
    </>
  );
}

/** 单选对象的三变换 gizmo。runtimeRegistry 是异步就绪的（模型加载完才有 group 引用），
 *  所以订阅其变更事件，就绪后才渲染控件 */
function Gizmo({ mode }: { mode: GizmoMode }) {
  const [version, setVersion] = useState(0);
  useEffect(() => subscribeRuntime(() => setVersion((v) => v + 1)), []);

  const selId = useDirectorStore((s) => s.selectedObjectIds[0] ?? null);
  const multi = useDirectorStore((s) => s.selectedObjectIds.length > 1);
  const boneEditing = useDirectorStore((s) => s.selectedBoneName !== null);
  const gridSnap = useDirectorStore((s) => s.project.scene.gridSnap);
  const updateObject = useDirectorStore((s) => s.updateObject);

  void version; // 订阅触发重渲即可，值本身不消费
  // 逐骨编辑时让位给 BoneGizmo（否则整对象 gizmo 与单骨 gizmo 叠加）
  if (selId === null || multi || boneEditing) {
    return null;
  }
  const entry = getRuntime(selId);
  if (entry === null) {
    return null;
  }
  const target = entry.group;

  function commit(): void {
    if (selId === null) {
      return;
    }
    updateObject(selId, {
      transform: {
        position: [target.position.x, target.position.y, target.position.z],
        rotation: [
          target.rotation.x * RAD_TO_DEG,
          target.rotation.y * RAD_TO_DEG,
          target.rotation.z * RAD_TO_DEG,
        ],
        scale: [target.scale.x, target.scale.y, target.scale.z],
      },
    });
  }

  return (
    <TransformControls
      object={target as Object3D}
      mode={mode}
      size={0.8}
      translationSnap={gridSnap ? GRID_SNAP_STEP_M : null}
      onObjectChange={commit}
    />
  );
}

/** 导出桥：把“当前 gl 画布 → PNG data-URI”的取图函数挂到外部 ref（preserveDrawingBuffer 保证随时可取）。 */
function CaptureBridge({ captureRef }: { captureRef: Props['captureRef'] }) {
  const { gl } = useThree();
  useEffect(() => {
    if (captureRef === undefined) {
      return;
    }
    captureRef.current = () => {
      try {
        return gl.domElement.toDataURL('image/png');
      } catch {
        return null;
      }
    };
    return () => {
      captureRef.current = null;
    };
  }, [gl, captureRef]);
  return null;
}

export function DirectorViewport({ preset, gizmoMode, onError, onPickDebug, captureRef }: Props) {
  const scene = useDirectorStore((s) => s.project.scene);
  const objects = useDirectorStore((s) => s.project.objects);
  const dpr = effectivePixelRatio(preset, window.devicePixelRatio);
  const groundOpacity = Math.min(scene.ground.opacity, preset.groundOpacity);
  const showGround = scene.ground.visible && groundOpacity > 0;
  // 导出态：隐藏所有编辑叠加层（网格/选中框/gizmo/机位标记/PiP），只留场景本体，截干净关键帧图
  const capturing = useDirectorStore((s) => s.capturing);

  return (
    <div
      className="h-full w-full"
      style={{ touchAction: 'none' }}
      onContextMenu={(e) => e.preventDefault()}
      onDragStart={(e) => e.preventDefault()}
      onMouseDownCapture={(e) => {
        // Edge「按钮手势」预防（右键拖拽=后退/前进）；压不住也无需根治——
        // 右键已让位给将来的上下文菜单，平移由中键承担（2026-09-28 定案）
        if (e.button === 2) {
          e.preventDefault();
        }
      }}
    >
      <Canvas
        dpr={dpr}
        gl={{ antialias: preset.antialias, preserveDrawingBuffer: true }}
        camera={{ position: [2.6, 1.8, 3.2], fov: 45, near: 0.05, far: 500 }}
      >
        <CaptureBridge captureRef={captureRef} />
        <color attach="background" args={[scene.skyColor]} />
        {/* 中性视口光：只为"看得见、不变形"，不是布光设计（边界守则 1） */}
        <ambientLight intensity={1.05} />
        <directionalLight position={[4, 8, 3]} intensity={0.9} />

        {preset.showGrid && !capturing ? (
          <Grid
            position={[0, scene.ground.height + 0.001, 0]}
            cellSize={0.5}
            cellThickness={0.6}
            cellColor={scene.gridColor}
            sectionSize={2}
            sectionThickness={1}
            sectionColor={scene.gridColor}
            fadeDistance={scene.fadeEnd}
            fadeStrength={1.5}
            infiniteGrid
          />
        ) : null}

        {showGround ? (
          <mesh position={[0, scene.ground.height, 0]} rotation={[-Math.PI / 2, 0, 0]}>
            <planeGeometry args={[200, 200]} />
            <meshStandardMaterial
              color={scene.groundColor}
              transparent
              opacity={groundOpacity}
              depthWrite={false}
            />
          </mesh>
        ) : null}

        {objects.map((o) => (
          <SceneObject
            key={o.id}
            obj={o}
            onError={onError}
          />
        ))}
        {!capturing && (
          <>
            <SelectionBoxes />
            <ObjectLabels />
            <Gizmo mode={gizmoMode} />
            <BoneGizmo />
            <CameraGizmos />
            <CameraGizmoControls mode={gizmoMode} />
            <CameraPiP />
          </>
        )}
        <ClickPicker onPickDebug={onPickDebug} />
        <CameraDirector />
        <ViewCommander />
        <OrbitControls
          target={[0, 1, 0]}
          makeDefault
          mouseButtons={{ LEFT: MOUSE.ROTATE, MIDDLE: MOUSE.PAN }}
        />
      </Canvas>
    </div>
  );
}
